/**
 * Helpers for ensuring the app server is ready before login.
 * When the prod VM is down, keep calling ensure-ready (server rotates machine
 * types like start-prod-app.sh) and wait until RUNNING + services buffer.
 */
import { SERVER_ADDR_COOKIE_NAME } from '../apicaller/api-consts';
import { readCookieValue } from '../utils/url-utils';

export interface EnsureServerReadyResult {
  success: boolean;
  status: string;
  waitedSeconds: number;
  message: string;
  started?: boolean;
}

/** After VM reports RUNNING, wait before login (matches server 80s buffer). */
const POST_RUNNING_SERVICES_READY_MS = 80_000;
const STATUS_POLL_INTERVAL_MS = 5_000;
/** Keep trying until the VM is ready (customer stays on the waiting screen). */
const ENSURE_READY_MAX_ROUNDS = 40;

function resolveApiBaseUrl(): string {
  const cookieServerAddr = readCookieValue(SERVER_ADDR_COOKIE_NAME);
  const serverUrl =
    cookieServerAddr && cookieServerAddr !== '.'
      ? cookieServerAddr
      : process.env.REACT_APP_SERVER_URL || '';

  if (!serverUrl || serverUrl === '.') {
    return '/api';
  }

  let base = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
  if (!base.endsWith('/api')) {
    base = `${base}/api`;
  }
  return base;
}

export function apiUrl(path: string): string {
  const cleanPath = String(path).replace(/^\/+/, '');
  return `${resolveApiBaseUrl()}/${cleanPath}`;
}

/** Nest TransformInterceptor wraps many responses as { data: T }. */
export function unwrapApiPayload<T>(json: unknown): T {
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data;
  }
  return json as T;
}

async function fetchAppServerStatus(): Promise<string | null> {
  try {
    const response = await fetch(apiUrl('gcp/app-server/status'), {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) return null;
    const payload = unwrapApiPayload<{ status?: string }>(
      await response.json().catch(() => ({})),
    );
    return payload?.status ?? null;
  } catch {
    return null;
  }
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll until the app VM reports RUNNING, then wait the services buffer.
 */
async function waitUntilRunningThenServicesReady(): Promise<boolean> {
  for (let attempt = 0; attempt < 36; attempt++) {
    const status = await fetchAppServerStatus();
    if (status === 'RUNNING') {
      await sleep(POST_RUNNING_SERVICES_READY_MS);
      return true;
    }
    await sleep(STATUS_POLL_INTERVAL_MS);
  }
  return false;
}

async function callEnsureReadyOnce(): Promise<{
  ok: boolean;
  payload: EnsureServerReadyResult | null;
  status: number;
}> {
  try {
    const response = await fetch(apiUrl('gcp/app-server/ensure-ready'), {
      method: 'POST',
      credentials: 'include',
    });
    const payload = unwrapApiPayload<EnsureServerReadyResult>(
      await response.json().catch(() => ({})),
    );
    return {
      ok: response.ok && Boolean(payload?.success),
      payload: payload || null,
      status: response.status,
    };
  } catch {
    return { ok: false, payload: null, status: 0 };
  }
}

/**
 * Auto-starts the app VM if needed and waits until it is ready.
 * Retries until success so the customer stays on the waiting screen while
 * the server rotates machine types (stockout) and boots services.
 * After status RUNNING, waits 80s before returning so MEA can finish starting.
 */
export async function ensureServerReady(): Promise<EnsureServerReadyResult> {
  let lastMessage = 'Unable to prepare session';

  for (let round = 0; round < ENSURE_READY_MAX_ROUNDS; round++) {
    const { ok, payload, status } = await callEnsureReadyOnce();
    if (ok && payload) {
      return payload;
    }
    if (payload?.message) lastMessage = payload.message;

    // Gateway timed out or start still in progress — poll status, then retry.
    if (
      status === 504 ||
      status === 502 ||
      status === 503 ||
      status === 0 ||
      !ok
    ) {
      const ready = await waitUntilRunningThenServicesReady();
      if (ready) {
        return {
          success: true,
          status: 'RUNNING',
          waitedSeconds: Math.round(POST_RUNNING_SERVICES_READY_MS / 1000),
          message: 'Server is ready',
          started: false,
        };
      }
      // Still down — kick another ensure-ready round (machine-type retries).
      await sleep(3_000);
      continue;
    }

    await sleep(5_000);
  }

  throw new Error(lastMessage);
}
